import {
  ActionReducerMapBuilder,
  EntityState,
  createAsyncThunk,
} from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';

import { AppError } from '@vk-hr-tek/core/error';

import { AttorneyCompany, UsersService } from '@app/gen/attorneys';
import { ThunkExtra } from '@app/store';
import { CompanyEventsStat, CompanyGroupUsers } from '@app/gen/settings';
import { Entity } from '@app/types';

import { SettingsState, SettingsWithRootState } from '../../settings.state';

export const getAttorneysCompanies = createAsyncThunk<
  AttorneyCompany[],
  void,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/attorneys/getCompanies',
  async (_, { rejectWithValue, getState, extra: { inject } }) => {
    try {
      const companies = getState().settings.attorneysCompanies.items;

      if (companies.length) return companies;

      const service = inject(UsersService);

      const result = await service.attorneyCompanyList();

      return result.companies;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const settingsAttorneysUsersReducers = (
  builder: ActionReducerMapBuilder<
    EntityState<Entity<CompanyEventsStat | CompanyGroupUsers>, string> &
      SettingsState
  >,
) => {
  builder.addCase(getAttorneysCompanies.pending, (state) => {
    state.attorneysCompanies.status = 'loading';
    state.attorneysCompanies.error = null;
  });
  builder.addCase(getAttorneysCompanies.fulfilled, (state, { payload }) => {
    state.attorneysCompanies.status = 'complete';
    state.attorneysCompanies.error = null;
    state.attorneysCompanies.items = payload;
  });
  builder.addCase(
    getAttorneysCompanies.rejected,
    (state, { payload, error }) => {
      state.attorneysCompanies.status = 'failed';
      state.attorneysCompanies.error =
        payload ||
        ({
          info: (error && error.message) || 'Unknown error',
          status: 500,
          source: 'client',
          title: 'Internal client error',
        } as AppError);
    },
  );
};
